import * as React from "react"
import { Track } from "livekit-client"
import type { CaptureOptionsBySource, ToggleSource } from "@livekit/components-core"
import type { TrackPublishOptions } from "livekit-client"
import { liveButtonStyle } from "./TrackToggleM"
import { MediaDeviceSelectM } from "../components/media-device/MediaDeviceSelectM"
import { View } from "react-native"
import { theme } from "../../theme/theme"
import FontAwesome6 from "react-native-vector-icons/FontAwesome6"
import { useMaybeRoomContext, useTrackToggle } from "@livekit/components-react"

// Icon helper
function getSourceIcon(source: Track.Source, enabled: boolean) {
    switch (source) {
        case Track.Source.Microphone:
            return enabled ? (
                <FontAwesome6 name={"microphone"} size={20} color={"white"} />
            ) : (
                <FontAwesome6
                    name={"microphone-slash"}
                    size={20}
                    color={theme.colors.primary}
                />
            )
        case Track.Source.Camera:
            return enabled ? (
                <FontAwesome6 name={"video"} size={20} color={"white"} />
            ) : (
                <FontAwesome6
                    name={"video-slash"}
                    size={20}
                    color={theme.colors.primary}
                />
            )
        default:
            return null
    }
}

/** @public */
export interface TrackToggleWithDeviceSelectProps<T extends ToggleSource> {
    source: T
    showIcon?: boolean
    initialState?: boolean
    onChange?: (enabled: boolean, isUserInitiated: boolean) => void
    captureOptions?: CaptureOptionsBySource<T>
    publishOptions?: TrackPublishOptions
    onDeviceError?: (error: Error) => void
    disabled?: boolean
    onActiveDeviceChange?: (deviceId: string) => void
    isSoundMode?: boolean
    onOpenHelp?: () => void
}

/**
 * Combines TrackToggle with device selection dropdown.
 * Allows users to toggle track on/off AND select which device to use.
 *
 * Pattern inspired by LiveKit's official ControlBar implementation:
 * https://github.com/livekit/components-js/blob/main/packages/react/src/prefabs/ControlBar.tsx
 *
 * @example
 * ```tsx
 * <TrackToggleWithDeviceSelectM
 *   source={Track.Source.Microphone}
 *   onChange={handleMicChange}
 * />
 * ```
 */
export function TrackToggleWithDeviceSelectM<T extends ToggleSource>({
    source,
    showIcon = true,
    initialState,
    onChange,
    captureOptions,
    publishOptions,
    onDeviceError,
    disabled,
    onActiveDeviceChange,
    isSoundMode,
    onOpenHelp,
}: TrackToggleWithDeviceSelectProps<T>) {
    const [isDeviceSelectOpen, setIsDeviceSelectOpen] = React.useState(false)
    const room = useMaybeRoomContext()

    // Get track toggle state and button props
    const { buttonProps, enabled } = useTrackToggle({
        source,
        onChange,
        captureOptions,
        publishOptions,
        onDeviceError,
    })

    // Map Track.Source to MediaDeviceKind
    const getDeviceKind = (source: Track.Source): MediaDeviceKind | undefined => {
        switch (source) {
            case Track.Source.Microphone:
                return "audioinput"
            case Track.Source.Camera:
                return "videoinput"
            default:
                return undefined
        }
    }

    const deviceKind = getDeviceKind(source)

    // Get the corresponding track for device selection
    const track = React.useMemo(() => {
        if (!room) return undefined
        const participant = room.localParticipant
        switch (source) {
            case Track.Source.Microphone:
                return participant.getTrackPublication(Track.Source.Microphone)?.audioTrack
            case Track.Source.Camera:
                return participant.getTrackPublication(Track.Source.Camera)?.videoTrack
            default:
                return undefined
        }
    }, [room, source])

    const handleDeviceChange = (deviceId: string) => {
        onActiveDeviceChange?.(deviceId)
        setIsDeviceSelectOpen(false)
    }

    // Determine colors based on enabled state
    const buttonBgColor = enabled ? theme.colors.primary : theme.colors.lighter
    const chevronColor = enabled ? theme.colors.white : theme.colors.primary

    return (
        <View
            style={{
                position: "relative",
            }}
        >
            {/* Combined button: toggle + device selector */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    borderRadius: 20,
                    overflow: "hidden",
                }}
            >
                {/* Main toggle button - slightly reduced */}
                <button
                    {...buttonProps}
                    style={{
                        ...liveButtonStyle,
                        backgroundColor: buttonBgColor,
                        width: 44,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                    }}
                    disabled={disabled}
                >
                    {showIcon && getSourceIcon(source, enabled)}
                </button>

                {/* Chevron dropdown button - always enabled for device configuration */}
                {deviceKind && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            setIsDeviceSelectOpen(!isDeviceSelectOpen)
                        }}
                        style={{
                            ...liveButtonStyle,
                            backgroundColor: buttonBgColor,
                            width: 26,
                            borderLeft: `1px solid ${enabled ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)"}`,
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                        }}
                        disabled={false}
                        aria-label="Select device"
                    >
                        <FontAwesome6
                            name={"chevron-up"}
                            size={11}
                            color={chevronColor}
                        />
                    </button>
                )}
            </div>

            {/* Device selection modal */}
            {deviceKind && (
                <MediaDeviceSelectM
                    kind={deviceKind}
                    track={track}
                    onActiveDeviceChange={handleDeviceChange}
                    onDeviceSelectError={onDeviceError}
                    requestPermissions={true}
                    isOpen={isDeviceSelectOpen}
                    setIsOpen={setIsDeviceSelectOpen}
                    includeAudioOutput={true}
                    isSoundMode={isSoundMode}
                    onOpenHelp={onOpenHelp}
                />
            )}
        </View>
    )
}
